import React, { useState, useEffect } from 'react';
import {
  ShieldAlert,
  FileText,
  Plus,
  Search,
  Edit2,
  Trash2,
  Download,
  Loader2,
  X,
} from 'lucide-react';
import { laborSafetyApi, usersApi } from '../api';
import { toast } from 'sonner';
import { extractApiError, validateDateFields, formatDateBR } from '../utils/dateValidation';
import Inspections from './Inspections';

// Cada formulário de Segurança do Trabalho tem seu próprio layout de colunas,
// então os campos ficam guardados como JSON (`data`) no backend. Este schema
// é a única fonte de verdade: ele define tanto o modal de novo registro
// quanto as colunas da tabela — para adicionar/ajustar campos de um
// formulário, mexe só aqui.
//
// F-007 é o modelo/padrão usado para os demais formulários "de linha"
// (F-010, F-065). F-026 (checklist) não usa este componente — ele mapeia
// direto para o módulo de Auto Inspeção, que já suporta checklists
// categorizados com S/N/NA/RNC.
const FORM_FIELDS = {
  'F-007': {
    title: 'Relação de Produtos Químicos Transportados',
    columns: [
      { key: 'cliente', label: 'Cliente', type: 'text', required: true },
      { key: 'codigo_onu', label: 'Código do produto (ONU)', type: 'text' },
      { key: 'descricao', label: 'Descrição/Nome do produto', type: 'text', required: true },
      { key: 'data_primeiro_transporte', label: 'Data do Primeiro Transporte', type: 'date' },
      { key: 'fds', label: 'FDS', type: 'text' },
      { key: 'incompatibilidade_quimica', label: 'Incompatibilidade Química', type: 'text' },
      { key: 'analise_risco', label: 'Análise do Risco', type: 'text' },
      { key: 'inclusao_matrizes', label: 'Inclusão nas Matrizes de Perigo/Risco e Aspecto/Impacto', type: 'select', options: ['Sim', 'Não'], span: 2 },
    ],
  },
  'F-010': {
    title: 'Levantamento de Perigo e Risco Ocupacional',
    columns: [
      { key: 'local', label: 'Local', type: 'text', required: true },
      { key: 'setor', label: 'Setor', type: 'text' },
      { key: 'atividade', label: 'Atividade', type: 'text', required: true },
      { key: 'perigo', label: 'Perigo', type: 'text', required: true },
      { key: 'dano', label: 'Dano', type: 'text' },
      { key: 'responsabilidade', label: 'Responsabilidade', type: 'select', options: ['Direta', 'Indireta'] },
      { key: 'situacao', label: 'Situação', type: 'select', options: ['Normal', 'Anormal', 'Emergencial'] },
      { key: 'temporariedade', label: 'Temporariedade', type: 'select', options: ['Presente', 'Eventual'] },
      { key: 'severidade', label: 'Severidade', type: 'number' },
      { key: 'probabilidade', label: 'Probabilidade', type: 'number' },
      { key: 'classificacao_risco', label: 'Classificação do Risco', type: 'select', options: ['Baixo - Não Significativo', 'Médio - Significativo', 'Alto - Significativo'] },
      { key: 'eliminacao', label: 'Eliminação', type: 'text' },
      { key: 'substituicao', label: 'Substituição', type: 'text' },
      { key: 'controle_engenharia', label: 'Controle de Engenharia', type: 'text' },
      { key: 'sinalizacao_epc', label: 'Sinalização/EPC', type: 'text' },
      { key: 'epi', label: 'EPI', type: 'text' },
      { key: 'controles', label: 'Controles', type: 'textarea', span: 2 },
      { key: 'plano_emergencia', label: 'Plano de Emergência', type: 'textarea', span: 2 },
    ],
  },
  'F-026': {
    title: 'Checklist de Autoinspeção de Segurança',
    useInspections: true,
  },
  'F-029': {
    title: 'Relatório de Ocorrência - SSMA',
    columns: [
      { key: 'tipo', label: 'Tipo', type: 'select', options: ['Acidente', 'Incidente'], required: true },
      { key: 'data_evento', label: 'Data do Evento', type: 'date', required: true },
      { key: 'horario_evento', label: 'Horário do Evento', type: 'time' },
      { key: 'local_ocorrencia', label: 'Local da Ocorrência', type: 'text' },
      { key: 'envolvidos_tipo', label: 'Envolvidos', type: 'select', options: ['Place', 'Terceiros'] },
      { key: 'numero_ros', label: 'Número da ROS', type: 'text' },
      { key: 'numero_cat', label: 'Número da CAT', type: 'text' },
      { key: 'nome_envolvido', label: 'Nome do(s) Lesionado(s)/Envolvido(s)', type: 'text' },
      { key: 'matricula_re', label: 'Matrícula (RE)', type: 'text' },
      { key: 'idade', label: 'Idade', type: 'number' },
      { key: 'funcao', label: 'Função', type: 'text' },
      { key: 'tempo_funcao', label: 'Tempo na Função', type: 'text' },
      { key: 'categoria_evento', label: 'Categoria', type: 'select', options: ['Lesão ou Doença', 'Dano à Propriedade', 'Dano Ambiental', 'Incidente'] },
      { key: 'natureza', label: 'Natureza da Lesão/Doença/Dano', type: 'text' },
      { key: 'objeto_relacionado', label: 'Objeto Relacionado', type: 'text' },
      { key: 'situacao_geradora', label: 'Situação Geradora', type: 'textarea', span: 2 },
      { key: 'descricao_evento', label: 'Descrição do Evento', type: 'textarea', span: 2 },
      { key: 'potencial_gravidade', label: 'Potencial de Gravidade', type: 'select', options: ['Baixo', 'Médio', 'Alto', 'Altíssimo'] },
      { key: 'causas_apuradas', label: 'Causas Apuradas', type: 'textarea', span: 2 },
      { key: 'acoes_preventivas', label: 'Ações Preventivas', type: 'textarea', span: 2 },
      { key: 'evidencias_fotograficas', label: 'Evidências Fotográficas', type: 'textarea', span: 2 },
    ],
  },
  'F-065': {
    title: 'Matriz de Levantamento de Aspectos e Impactos Ambientais',
    columns: [
      { key: 'area', label: 'Área', type: 'text', required: true },
      { key: 'atividades', label: 'Atividades', type: 'text', required: true },
      { key: 'aspecto', label: 'Aspecto', type: 'text', required: true },
      { key: 'impacto', label: 'Impacto', type: 'text' },
      { key: 'caracterizacao', label: 'Caracterização', type: 'text' },
      { key: 'responsabilidade', label: 'Responsabilidade', type: 'select', options: ['Direta', 'Indireta'] },
      { key: 'situacao', label: 'Situação', type: 'select', options: ['Normal', 'Anormal', 'Emergencial'] },
      { key: 'temporariedade', label: 'Temporariedade', type: 'select', options: ['Presente', 'Eventual'] },
      { key: 'severidade', label: 'Severidade', type: 'number' },
      { key: 'frequencia_probabilidade', label: 'Frequência/Probabilidade', type: 'number' },
      { key: 'classificacao', label: 'Classificação', type: 'text' },
      { key: 'descricao_requisito', label: 'Descrição do Requisito Aplicável', type: 'textarea', span: 2 },
      { key: 'controles', label: 'Controles', type: 'textarea', span: 2 },
    ],
  },
};

const FORM_CODES = Object.keys(FORM_FIELDS);

const STATUS_LABELS = {
  regular: { label: 'Regular', color: 'bg-green-500/20 text-green-400' },
  a_vencer: { label: 'A Vencer', color: 'bg-yellow-500/20 text-yellow-400' },
  vencido: { label: 'Vencido', color: 'bg-red-500/20 text-red-400' },
};

const LaborSafety = () => {
  const [activeForm, setActiveForm] = useState(FORM_CODES[0]);
  const schema = FORM_FIELDS[activeForm];

  return (
    <div className="space-y-6" data-testid="labor-safety-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
          <ShieldAlert className="text-green-500" />
          Segurança do Trabalho
        </h1>
        <p className="text-gray-400 mt-1">Formulários de Segurança do Trabalho</p>
      </div>

      {/* Tabs — one per form */}
      <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
        {FORM_CODES.map((code) => (
          <button
            key={code}
            onClick={() => setActiveForm(code)}
            title={code}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${
              activeForm === code
                ? 'border-green-400 text-green-400'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <FileText size={18} />
            {FORM_FIELDS[code].title}
          </button>
        ))}
      </div>

      {schema.useInspections ? (
        // F-026 é um checklist categorizado (S/N/NA/RNC) — o mesmo formato já
        // suportado pelo módulo de Auto Inspeção, então em vez de duplicar
        // essa estrutura aqui, a aba embute a própria página.
        <Inspections />
      ) : (
        <LaborSafetyFormPanel key={activeForm} formCode={activeForm} schema={schema} />
      )}
    </div>
  );
};

const LaborSafetyFormPanel = ({ formCode, schema }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, overdue: 0, due_soon: 0, regular: 0 });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  useEffect(() => {
    loadData();
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [itemsRes, statsRes] = await Promise.all([
        laborSafetyApi.list(formCode, statusFilter),
        laborSafetyApi.stats(formCode),
      ]);
      setItems(itemsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await usersApi.list({ limit: 100 });
      setUsers(res.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleCreate = async (data, meta) => {
    try {
      await laborSafetyApi.create(formCode, data, meta);
      toast.success('Registro criado com sucesso');
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar registro'));
    }
  };

  const handleUpdate = async (data, meta) => {
    try {
      await laborSafetyApi.update(editingItem.id, data, meta);
      toast.success('Registro atualizado com sucesso');
      setShowModal(false);
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      await laborSafetyApi.delete(confirmDelete.id);
      toast.success('Registro excluído');
      setConfirmDelete(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir registro');
    }
  };

  const triggerDownload = (blobData, filename) => {
    const url = window.URL.createObjectURL(new Blob([blobData], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = async (item) => {
    try {
      const res = await laborSafetyApi.download(item.id);
      triggerDownload(res.data, `${item.code}.pdf`);
    } catch (error) {
      toast.error('Erro ao baixar registro');
    }
  };

  const handleDownloadAll = async () => {
    if (items.length === 0) {
      toast.error('Nenhum registro para baixar');
      return;
    }
    setDownloadingAll(true);
    try {
      const res = await laborSafetyApi.downloadAll(formCode);
      triggerDownload(res.data, `${formCode}.pdf`);
    } catch (error) {
      toast.error('Erro ao baixar registros');
    } finally {
      setDownloadingAll(false);
    }
  };

  const filteredItems = items.filter(i => {
    if (!searchTerm) return true;
    const haystack = `${i.code} ${Object.values(i.data || {}).join(' ')}`.toLowerCase();
    return haystack.includes(searchTerm.toLowerCase());
  });

  const formatCell = (col, value) => {
    if (value === undefined || value === null || value === '') return '-';
    if (col.type === 'date') return formatDateBR(value);
    return String(value);
  };

  return (
    <div className="space-y-6">
      {/* Form identification */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-3">
          <FileText className="text-green-500" />
          {schema.title}
        </h2>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <button
          onClick={handleDownloadAll}
          disabled={downloadingAll || items.length === 0}
          className="btn-secondary flex items-center gap-2 disabled:opacity-50"
        >
          {downloadingAll ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
          Baixar Todos
        </button>
        <button
          onClick={() => { setEditingItem(null); setShowModal(true); }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={20} />
          Novo Registro
        </button>
      </div>

      {/* Stats Cards — validade dos registros */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4"><p className="text-gray-400 text-sm">Total de Registros</p><p className="text-2xl font-bold text-white">{stats.total}</p></div>
        <div className="card p-4"><p className="text-gray-400 text-sm">Vencidos</p><p className="text-2xl font-bold text-white">{stats.overdue}</p></div>
        <div className="card p-4"><p className="text-gray-400 text-sm">A Vencer (30 dias)</p><p className="text-2xl font-bold text-white">{stats.due_soon}</p></div>
        <div className="card p-4"><p className="text-gray-400 text-sm">Regulares</p><p className="text-2xl font-bold text-white">{stats.regular}</p></div>
      </div>

      {/* Content */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : (
          <div className="space-y-4 p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Buscar registros..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10 w-full"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input px-3 w-full sm:w-60"
              >
                <option value="">Todos os Status</option>
                <option value="regular">Regular</option>
                <option value="a_vencer">A Vencer</option>
                <option value="vencido">Vencido</option>
              </select>
            </div>

            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Código</th>
                    {schema.columns.map((col) => (
                      <th key={col.key}>{col.label}</th>
                    ))}
                    <th>Validade</th>
                    <th>Status</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan={schema.columns.length + 4} className="py-8 text-center text-gray-500">
                        Nenhum registro encontrado
                      </td>
                    </tr>
                  ) : (
                    filteredItems.map((item) => (
                      <tr key={item.id}>
                        <td className="font-mono text-white whitespace-nowrap">{item.code}</td>
                        {schema.columns.map((col) => (
                          <td key={col.key} className="text-gray-400 max-w-xs truncate" title={formatCell(col, item.data?.[col.key])}>
                            {formatCell(col, item.data?.[col.key])}
                          </td>
                        ))}
                        <td className={item.status === 'vencido' ? 'text-red-400 font-semibold whitespace-nowrap' : 'text-gray-400 whitespace-nowrap'}>
                          {formatDateBR(item.next_date)}
                        </td>
                        <td>
                          <span className={`px-2 py-1 rounded-full text-xs whitespace-nowrap ${STATUS_LABELS[item.status]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                            {STATUS_LABELS[item.status]?.label || item.status}
                          </span>
                        </td>
                        <td>
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => handleDownload(item)} className="p-1 text-gray-400 hover:text-green-400" title="Baixar">
                              <Download size={18} />
                            </button>
                            <button onClick={() => { setEditingItem(item); setShowModal(true); }} className="p-1 text-gray-400 hover:text-blue-400" title="Editar">
                              <Edit2 size={18} />
                            </button>
                            <button onClick={() => setConfirmDelete(item)} className="p-1 text-gray-400 hover:text-red-400" title="Excluir">
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <LaborSafetyRecordModal
          formCode={formCode}
          schema={schema}
          item={editingItem}
          users={users}
          onClose={() => { setShowModal(false); setEditingItem(null); }}
          onSave={editingItem ? handleUpdate : handleCreate}
        />
      )}

      {/* Delete Confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-sm w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-500/20">
                <Trash2 size={22} className="text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Confirmar Exclusão</h3>
            </div>
            <p className="text-gray-400 text-sm">
              Tem certeza que deseja excluir <span className="text-white font-medium">{confirmDelete.code}</span>?
              Esta ação não poderá ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="btn-secondary">Voltar</button>
              <button onClick={handleConfirmDelete} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors">
                <Trash2 size={16} />
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const LaborSafetyRecordModal = ({ formCode, schema, item, users, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const initial = {};
  schema.columns.forEach((col) => { initial[col.key] = item?.data?.[col.key] ?? ''; });
  const [formData, setFormData] = useState(initial);
  const [nextDate, setNextDate] = useState(item?.next_date || '');
  const [responsibleId, setResponsibleId] = useState(item?.responsible_id || '');

  const setField = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    const missing = schema.columns.find((col) => col.required && !String(formData[col.key] || '').trim());
    if (missing) { toast.error(`Preencha o campo "${missing.label}"`); return; }

    const dateFields = schema.columns
      .filter((col) => col.type === 'date' && formData[col.key])
      .map((col) => ({ value: formData[col.key], label: col.label }));
    if (nextDate) dateFields.push({ value: nextDate, label: 'Próxima Validade' });
    const dateError = validateDateFields(dateFields);
    if (dateError) { toast.error(dateError); return; }

    setSaving(true);
    try {
      await onSave(formData, { nextDate, responsibleId });
    } finally {
      setSaving(false);
    }
  };

  const renderInput = (col) => {
    const value = formData[col.key];
    if (col.type === 'select') {
      return (
        <select value={value} onChange={(e) => setField(col.key, e.target.value)} className="input w-full">
          <option value="">Selecione...</option>
          {col.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }
    if (col.type === 'textarea') {
      return (
        <textarea
          value={value}
          onChange={(e) => setField(col.key, e.target.value)}
          className="input w-full"
          rows={3}
        />
      );
    }
    return (
      <input
        type={col.type}
        value={value}
        onChange={(e) => setField(col.key, e.target.value)}
        className="input w-full"
        min={col.type === 'date' ? '1900-01-01' : undefined}
        max={col.type === 'date' ? '2099-12-31' : undefined}
      />
    );
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            {item ? `Editar Registro — ${formCode}` : `Novo Registro — ${formCode}`}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {schema.columns.map((col) => (
              <div key={col.key} className={col.span === 2 ? 'md:col-span-2' : ''}>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {col.label}{col.required && ' *'}
                </label>
                {renderInput(col)}
              </div>
            ))}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Próxima Validade</label>
              <input
                type="date"
                value={nextDate}
                onChange={(e) => setNextDate(e.target.value)}
                className="input w-full"
                min="1900-01-01"
                max="2099-12-31"
                placeholder="Próxima revisão/renovação, se houver"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Responsável</label>
              <select
                value={responsibleId}
                onChange={(e) => setResponsibleId(e.target.value)}
                className="input w-full"
              >
                <option value="">Selecione...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.full_name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default LaborSafety;
